// React + Vite: four small screens with shared sign-in state are simplest in the framework any successor already knows, and Vite gives a fast build plus the mock-mode dev server with no extra tooling.

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Api } from './api.ts';
import { App } from './App.tsx';
import { clearSignedOut, cognitoAuth, mockAuth } from './auth.ts';
import { loadConfig } from './config.ts';
import './styles.css';

const root = createRoot(document.getElementById('root')!);

async function start(): Promise<void> {
  const config = await loadConfig();
  if (config.mock) {
    const params = new URLSearchParams(location.search);
    const asRole = params.get('as');
    if (asRole === 'reviewer' || asRole === 'admin') sessionStorage.setItem('mockRole', asRole);
    const role = sessionStorage.getItem('mockRole') ?? 'admin';
    root.render(
      <StrictMode>
        <App api={new Api('/api', mockAuth, role)} auth={mockAuth} mock />
      </StrictMode>,
    );
    return;
  }
  const auth = await cognitoAuth(config.cognitoDomain, config.clientId, { identityProvider: config.identityProvider, adminClientId: config.adminClientId });
  if (!auth) return; // redirecting to sign-in
  if (auth === 'signed-out') {
    document.title = 'Signed out · Four Winds Sync';
    root.render(<SignedOut xero={config.identityProvider === 'Xero'} />);
    return;
  }
  root.render(
    <StrictMode>
      <App api={new Api(config.apiUrl, auth)} auth={auth} mock={false} />
    </StrictMode>,
  );
}

function SignedOut({ xero }: { xero: boolean }) {
  return (
    <main className="signed-out">
      <div className="signed-out-card">
        <h1>You're signed out</h1>
        {xero && <p>You're signed out of Xero too.</p>}
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            clearSignedOut();
            location.assign('/');
          }}
        >
          Sign in again
        </button>
      </div>
    </main>
  );
}

start().catch((err: unknown) => {
  root.render(<p className="fatal">{err instanceof Error ? err.message : String(err)}</p>);
});
